import { ReactNode, useMemo, useState } from 'react';
import {
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  ListButton,
  Pagination,
  ReferenceField,
  ReferenceManyField,
  Show,
  ShowButton,
  TextField,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { ESTDateField } from 'src/client/components/custom-ra-fields/est-date-field';
import { formatCampaignTitle } from '../campaigns/CampaignList';
import {
  LineItemRecord,
  formatLineItemTitle,
  invoiceStatusChoices,
  lineItemStatusChoices,
} from '../line-items/LineItemCreate';
import { ProductArchiveButton } from './ProductArchiveButton';
import { formatDefaultTargetingForInput } from './json';

type ProductRecord = {
  id?: string;
  name?: string | null;
  externalPioId?: string | null;
  defaultTargeting?: unknown;
};

const formatChoice = (choices: { id: string; name: string }[], value?: string | null) =>
  choices.find(choice => choice.id === value)?.name ?? value ?? '-';

const sectionGrid = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
  gap: 2,
} as const;

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box minWidth={0}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Box sx={{ '& .MuiTypography-root': { fontSize: 'body2.fontSize' } }}>{children}</Box>
  </Box>
);

const ProductShowActions = () => (
  <TopToolbar>
    <ListButton />
    <EditButton />
    <ProductArchiveButton />
  </TopToolbar>
);

const ProductHeader = () => {
  const record = useRecordContext<ProductRecord>();
  if (!record) return null;
  return (
    <CardContent>
      <CardHeader title={record.name ?? 'Product'} sx={{ px: 0, pt: 0, pb: 1 }} />
    </CardContent>
  );
};

const OverviewSection = () => (
  <CardContent>
    <CardHeader title="Overview" sx={{ px: 0, pt: 0, pb: 1 }} />
    <Box sx={sectionGrid}>
      <Field label="Name">
        <TextField source="name" emptyText="-" />
      </Field>
      <Field label="External PIO ID">
        <TextField source="externalPioId" emptyText="-" />
      </Field>
      <Field label="Product ID">
        <TextField source="id" emptyText="-" />
      </Field>
    </Box>
  </CardContent>
);

const DefaultTargetingSection = () => {
  const record = useRecordContext<ProductRecord>();
  const formatted = formatDefaultTargetingForInput(record?.defaultTargeting);
  if (!formatted) return null;
  return (
    <CardContent>
      <CardHeader title="Default Targeting" sx={{ px: 0, pt: 0, pb: 1 }} />
      <Box
        component="pre"
        sx={{
          bgcolor: 'grey.100',
          borderRadius: 1,
          overflow: 'auto',
          p: 2,
          whiteSpace: 'pre-wrap',
          fontSize: 'body2.fontSize',
        }}
      >
        {formatted}
      </Box>
    </CardContent>
  );
};

const AuditSection = () => (
  <CardContent>
    <CardHeader title="Audit" sx={{ px: 0, pt: 0, pb: 1 }} />
    <Box sx={sectionGrid}>
      <Field label="Created At">
        <ESTDateField source="createdAt" showTime />
      </Field>
      <Field label="Updated At">
        <ESTDateField source="updatedAt" showTime />
      </Field>
      <Field label="Archived At">
        <ESTDateField source="archivedAt" showTime emptyText="-" />
      </Field>
    </Box>
  </CardContent>
);

const LineItemsToolbar = ({
  filterValue,
  onFilterChange,
}: {
  filterValue: string;
  onFilterChange: (value: string) => void;
}) => {
  const record = useRecordContext<ProductRecord>();

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
      <MuiTextField
        label="Filter line items"
        value={filterValue}
        onChange={event => onFilterChange(event.target.value)}
        size="small"
        sx={{ minWidth: 260 }}
      />
      <CreateButton
        resource="lineItems"
        label="Add line item"
        to={{
          pathname: '/lineItems/create',
          search: record?.id ? `?productId=${record.id}` : undefined,
        }}
      />
    </Box>
  );
};

const LineItemsTable = () => {
  const [filterValue, setFilterValue] = useState('');
  const lineItemFilter = useMemo(() => (filterValue ? { name: filterValue } : {}), [filterValue]);

  return (
    <CardContent>
      <CardHeader title="Line Items Using This Product" sx={{ px: 0, pt: 0 }} />
      <LineItemsToolbar filterValue={filterValue} onFilterChange={setFilterValue} />
      <ReferenceManyField
        reference="lineItems"
        target="productId"
        sort={{ field: 'startDate', order: 'DESC' }}
        perPage={25}
        filter={lineItemFilter}
        pagination={<Pagination rowsPerPageOptions={[25, 50, 100]} />}
      >
        <Datagrid
          rowClick="show"
          bulkActionButtons={false}
          empty={<Box sx={{ p: 2 }}>No line items use this product.</Box>}
        >
          <FunctionField
            label="Line Item"
            source="name"
            render={(record: LineItemRecord) => formatLineItemTitle(record)}
          />
          <ReferenceField source="campaignId" reference="campaigns" label="Campaign" link="show">
            <FunctionField render={formatCampaignTitle} />
          </ReferenceField>
          <ESTDateField source="startDate" label="Start Date" />
          <ESTDateField source="endDate" label="End Date" />
          <FunctionField
            source="status"
            label="Status"
            render={(record: Record<string, any>) => formatChoice(lineItemStatusChoices, record?.status)}
          />
          <FunctionField
            source="invoiceStatus"
            label="Invoice Status"
            render={(record: Record<string, any>) => formatChoice(invoiceStatusChoices, record?.invoiceStatus)}
          />
          <TextField source="gamLineItemId" label="GAM Line Item ID" emptyText="-" />
          <ShowButton resource="lineItems" />
          <EditButton resource="lineItems" />
        </Datagrid>
      </ReferenceManyField>
    </CardContent>
  );
};

export const ProductShow = () => (
  <Show actions={<ProductShowActions />}>
    <ProductHeader />
    <Divider />
    <OverviewSection />
    <Divider />
    <DefaultTargetingSection />
    <Divider />
    <AuditSection />
    <Divider />
    <LineItemsTable />
  </Show>
);
